import type { JSX } from 'react'
import type { Freshness } from '../api'
import { freshnessSummary, staleLine } from '../freshness'

// Whether the numbers are current has to be answerable before any figure means
// anything: the sheet's failure mode was a number that looked fine and was
// three weeks old (invariant #3). The pill is always in the top bar, so the
// answer is on screen from every tab; the stale line interrupts the Overview
// only while a source is actually out of date; the chart notes say which
// charts are drawn from that old data.

export function FreshnessPill({
  freshness,
  onOpen,
}: {
  freshness: Freshness[]
  onOpen: () => void
}): JSX.Element {
  const { tone, text } = freshnessSummary(freshness)
  return (
    <button
      type="button"
      className={`freshness-pill ${tone}`}
      onClick={onOpen}
      aria-label={`${text}. Open data sources`}
    >
      <span className="dot" aria-hidden="true" />
      {text}
    </button>
  )
}

export function StaleLine({
  freshness,
  onOpen,
}: {
  freshness: Freshness[]
  // Absent on the scoped IT-costs page, which has no Data sources tab to open.
  onOpen?: () => void
}): JSX.Element | null {
  const text = staleLine(freshness)
  if (text === null) return null
  return (
    <p className="stale-line" role="alert">
      <span aria-hidden="true">⚠</span> {text}{' '}
      {onOpen !== undefined && (
        <button type="button" className="link-button" onClick={onOpen}>
          View details
        </button>
      )}
    </p>
  )
}

export function SourceNote({ text }: { text: string | null }): JSX.Element | null {
  return text === null ? null : <p className="source-note">{text}</p>
}
